import { useSelector } from 'react-redux';
import React, { useEffect, useState, useRef } from 'react';

import { SnackBarAlert } from 'shared';
import ClearIcon from '@mui/icons-material/Clear';
import { Box, Grid, Typography, Paper, IconButton } from '@mui/material';


import { useAuth } from 'hooks/useAuth';
import { identifyUser, trackEvent } from 'utils/mixpanel';
import { snackAlertData } from 'utils/constants';
import { handleErrorAlert } from 'utils/handleErrorAlert';
import CurrentMatchCard from './AiMatching/CurrentMatchCard';
import { handleSuccessAlert } from 'utils/handleSuccessAlert';
import CompletedMatchCard from './AiMatching/CompletedMatchCard';
import CurrentUserInputCard from './AiMatching/CurrentUserInputCard';
import AgentMatchingFilters from './AiMatching/AgentMatchingFilters';
import { useInventoryService } from 'store/services/inventory.service';
import CompletedUserInputCard from './AiMatching/CompletedUserInputCard';
import { AIMatchingViewStylesTable } from 'components/Inventory/style/style';
import { excludeTransactionsFromAgentMatching } from 'store/services/bank.Service';
import EmailSourceModal from '../../../InventoryEmails/components/Inventory/components/EmailSourceModal';

const AIMatchingView = ({ onExit }) => {
    const {
        getAgentMatchings,
        SkipAgentMatching,
        excludePlaidTransaction,
        matchAggFileInventoryWithPlaidTransaction,
    } = useInventoryService();
    const classes = AIMatchingViewStylesTable();
    const { id: companyId } = useSelector((state) => state.company.data);
    const { user: { data: userData } } = useAuth();

    const messagesEndRef = useRef(null);
    const [filterValues, setFilterValues] = useState({});
    const [noDataFound, setNoDataFound] = useState(false);
    const [loadingSkip, setLoadingSkip] = useState(false);
    const [introVisible, setIntroVisible] = useState(true);
    const [matchLoading, setMatchLoading] = useState(false);
    const [renderingStage, setRenderingStage] = useState(0);
    const [completedCards, setCompletedCards] = useState([]);
    const [alertData, setAlertData] = useState(snackAlertData);
    const [excludeLoading, setExcludeLoading] = useState(false);
    const [currentCardIndex, setCurrentCardIndex] = useState(-1);
    const [currentSection, setCurrentSection] = useState('matches');
    const [showFinalMessage, setShowFinalMessage] = useState(false);
    const [selectedEmailData, setSelectedEmailData] = useState(null);
    const [skipTransactionId, setSkipTransactionId] = useState(null);
    const [showEmailSourceModal, setShowEmailSourceModal] = useState(false);
    const [data, setData] = useState({ matches: [], needs_user_input: [] });
    const [doNotShowAgainLoading, setDoNotShowAgainLoading] = useState(false);

    const handleEmailSourceClick = (emailId) => {
        const emailData = emailId;
        if (
            !emailData.email_data ||
            (Array.isArray(emailData.email_data) && emailData.email_data.length === 0)
        ) {
            setAlertData({
                message:
                    'There were no emails or payment method details on emails. Please provide last 4 digits of payment method for each inventory.',
                open: true,
                severity: 'error',
            });
            return;
        }

        const emailIdsToSet = Array.isArray(emailData.email_data)
            ? emailData.email_data.map((item) => item.email_id)
            : [emailData.email_data.email_id];

        setSelectedEmailData(emailIdsToSet);
        setShowEmailSourceModal(true);
    };

    const startCardRendering = () => {
        if (data.matches && data.matches.length > 0 && currentSection === 'matches') {
            setCurrentCardIndex(0);
            setRenderingStage(1);
        } else if (
            data.needs_user_input &&
            data.needs_user_input.length > 0 &&
            currentSection === 'needs_user_input'
        ) {
            setCurrentCardIndex(0);
            setRenderingStage(1);
        } else if (data.matches && data.matches.length > 0) {
            setCurrentSection('matches');
            setCurrentCardIndex(0);
            setRenderingStage(1);
        } else if (data.needs_user_input && data.needs_user_input.length > 0) {
            setCurrentSection('needs_user_input');
            setCurrentCardIndex(0);
            setRenderingStage(1);
        }
    };

    const advanceRenderingStage = () => {
        if (currentSection === 'matches') {
            setRenderingStage((prev) => {
                if (prev === 3) {
                    setCompletedCards((prev) => [
                        ...prev,
                        { section: currentSection, index: currentCardIndex },
                    ]);

                    if (currentCardIndex < data.matches.length - 1) {
                        setTimeout(() => {
                            setCurrentCardIndex((prevIndex) => prevIndex + 1);
                            setRenderingStage(1);
                        }, 500);
                    } else if (data.needs_user_input && data.needs_user_input.length > 0) {
                        setTimeout(() => {
                            setCurrentSection('needs_user_input');
                            setCurrentCardIndex(0);
                            setRenderingStage(1);
                        }, 500);
                    }
                    return 4;
                }
                return prev + 1;
            });
        } else if (currentSection === 'needs_user_input') {
            setRenderingStage((prev) => {
                if (prev === 2) {
                    setCompletedCards((prev) => [
                        ...prev,
                        { section: currentSection, index: currentCardIndex },
                    ]);

                    if (currentCardIndex < data.needs_user_input.length - 1) {
                        setTimeout(() => {
                            setCurrentCardIndex((prevIndex) => prevIndex + 1);
                            setRenderingStage(1);
                        }, 500);
                    }
                    return 3;
                }
                return prev + 1;
            });
        }
    };

    const handleSkipAgentMatching = async (skipPlaidTransactionId, filters) => {
        try {
            setLoadingSkip(true);

            const response = await SkipAgentMatching({
                companyId,
                skipPlaidTransactionId,
                ...filters,
            });

            if (response?.matches || response?.needs_user_input) {
                const updatedData = {
                    matches: response.matches || [],
                    needs_user_input: response.needs_user_input || [],
                };

                setIntroVisible(true);

                setData(updatedData);

                setCurrentCardIndex(-1);
                setRenderingStage(0);
                setCompletedCards([]);

                const hasMatches = updatedData.matches && updatedData.matches.length > 0;
                const hasUserInput =
                    updatedData.needs_user_input && updatedData.needs_user_input.length > 0;

                if (hasMatches) {
                    setCurrentSection('matches');
                } else if (hasUserInput) {
                    setCurrentSection('needs_user_input');
                }

                const noMoreCards = !hasMatches && !hasUserInput;

                if (noMoreCards) {
                    setNoDataFound(true);
                    setAlertData({
                        message: '🎉 All transactions have been processed!',
                        open: true,
                        severity: 'success',
                    });
                    setShowFinalMessage(true);
                    setTimeout(() => {
                        onExit();
                    }, 3000);
                } else {
                    setTimeout(() => {
                        setIntroVisible(false);
                    }, 1000);

                    setTimeout(() => {
                        startCardRendering();
                    }, 1500);
                }

                setLoadingSkip(false);
            } else if (response?.message === 'There are no more bank transactions to match.') {
                setNoDataFound(true);
                setAlertData({
                    message: response.message,
                    open: true,
                    severity: 'success',
                });
                setShowFinalMessage(true);
                setTimeout(() => {
                    onExit();
                }, 3000);
                setLoadingSkip(false);
            } else {
                setLoadingSkip(false);
                setAlertData({
                    message: 'No more transactions to process.',
                    open: true,
                    severity: 'info',
                });
                setNoDataFound(true);
                setShowFinalMessage(true);
                setTimeout(() => {
                    onExit();
                }, 3000);
            }
        } catch (error) {
            setLoadingSkip(false);
            console.error('Error skipping match:', error);
            setAlertData({
                message: 'Failed to skip. Please try again.',
                open: true,
                severity: 'error',
            });
        }
    };

    const skipMatch = async (cardData, source) => {
        let skipPlaidTransactionId = null;

        if (source === 'matchCard') {
            skipPlaidTransactionId = cardData?.transaction_id;
        } else {
            if (cardData.transaction?.plaid_transaction_id) {
                skipPlaidTransactionId = cardData.transaction.plaid_transaction_id;
            }

            if (Array.isArray(cardData.transactions)) {
                const ids = cardData.transactions
                    .map((t) => t.id)
                    .filter((id) => typeof id === 'number' && !isNaN(id));
                if (ids.length > 0) {
                    skipPlaidTransactionId = Math.min(...ids);
                }
            }
        }

        if (!skipPlaidTransactionId) {
            console.warn('Missing Plaid transaction ID for skip.');
            return;
        }

        setSkipTransactionId(skipPlaidTransactionId);

        handleSkipAgentMatching(skipPlaidTransactionId, filterValues);
    };

    const handleMatchClick = async (values, cardData, source) => {
        let plaidTransactionId = null;
        let aggregatedFileInventoryId = null;

        if (source === 'matchCard') {
            plaidTransactionId = cardData.transaction_id;
            aggregatedFileInventoryId = cardData.matched_inventory.id;
        } else {
            plaidTransactionId = cardData.preserved_values.selectedTransactionId;
            aggregatedFileInventoryId = cardData.preserved_values.selectedInventoryId;
        }

        if (!plaidTransactionId || !aggregatedFileInventoryId) {
            console.warn('Missing IDs for matching');
            return;
        }

        setMatchLoading(true);

        try {
            const response = await matchAggFileInventoryWithPlaidTransaction({
                companyId,
                plaidTransactionId,
                aggregatedFileInventoryIds: [aggregatedFileInventoryId],
            });

            if (response.failed_matches && response.failed_matches.length) {
                const errorMessages = response.failed_matches
                    .map((f) => `Inventory ID ${f.inventory_id}: ${f.error}`)
                    .join('\n');
                setAlertData({
                    message: `Error matching the inventory:\n${errorMessages}`,
                    open: true,
                    severity: 'error',
                });
                return;
            }

            setAlertData({
                severity: 'success',
                message: response.message || 'Matched successfully!',
                open: true,
            });

            identifyUser(userData.email.toLowerCase(), { $email: userData.email });
            trackEvent('Matching', {
                source: "AI",
            });

            let willKeepCard = false;
            let updatedMatches = [...data.matches];
            let updatedNeedsUserInput = [...data.needs_user_input];

            if (source === 'userInputCard') {
                const original = data.needs_user_input.find((card) =>
                    card.transactions.some((t) => t.id.toString() === plaidTransactionId),
                );
                if (original) {
                    const newTxnCount = original.transactions.filter(
                        (t) => t.id.toString() !== plaidTransactionId,
                    ).length;
                    const newInvCount = original.matched_inventories.filter(
                        (inv) => inv.id.toString() !== aggregatedFileInventoryId,
                    ).length;
                    willKeepCard = newTxnCount > 0 && newInvCount > 0;
                }
            }

            if (source === 'matchCard') {
                updatedMatches = updatedMatches.filter(
                    (item) => item.transaction_id !== plaidTransactionId,
                );
            } else {
                const idx = updatedNeedsUserInput.findIndex((card) =>
                    card.transactions.some((t) => t.id.toString() === plaidTransactionId),
                );
                if (idx !== -1) {
                    const card = { ...updatedNeedsUserInput[idx] };
                    card.transactions = card.transactions.filter(
                        (t) => t.id.toString() !== plaidTransactionId,
                    );
                    card.matched_inventories = card.matched_inventories.filter(
                        (inv) => inv.id.toString() !== aggregatedFileInventoryId,
                    );
                    if (card.transactions.length && card.matched_inventories.length) {
                        card.preserved_values = {};
                        updatedNeedsUserInput[idx] = card;
                    } else {
                        updatedNeedsUserInput.splice(idx, 1);
                    }
                }
            }

            setData({ matches: updatedMatches, needs_user_input: updatedNeedsUserInput });

            if (source === 'userInputCard' && willKeepCard) {
                return;
            }

            const noMore = updatedMatches.length === 0 && updatedNeedsUserInput.length === 0;

            if (noMore) {
                setIntroVisible(true);
                setCurrentCardIndex(-1);
                setRenderingStage(0);
                setCompletedCards([]);

                await handleGetAgentMatchings(filterValues);

                setTimeout(() => {
                    setIntroVisible(false); // Hide intro after a short delay
                }, 1000);
            } else {
                if (updatedMatches.length > 0) {
                    setCurrentSection('matches');
                    setCurrentCardIndex(0);
                } else if (updatedNeedsUserInput.length > 0) {
                    setCurrentSection('needs_user_input');
                    setCurrentCardIndex(0);
                }
            }
        } catch (error) {
            console.error('Error in matchAggFileInventoryWithPlaidTransaction:', error);
            setAlertData({
                message: 'Error matching. Please try again.',
                open: true,
                severity: 'error',
            });
        } finally {
            setMatchLoading(false);
        }
    };

    const handleExcludeClick = async (cardData, source) => {
        try {
            setExcludeLoading(true);

            const plaid_transaction_ids = [];

            if (data.matches && data.matches?.length) {
                for (const match of data.matches) {
                    plaid_transaction_ids.push(match.transaction_id);
                }
            }

            if (data.needs_user_input && data.needs_user_input?.length) {
                for (const transaction of data.needs_user_input[0]?.transactions) {
                    plaid_transaction_ids.push(transaction.id);
                }
            }

            const response = await excludePlaidTransaction({
                company_id: companyId,
                plaid_transaction_ids,
            });

            handleSuccessAlert(setAlertData, response?.message);

            await skipMatch(cardData, source);
        } catch (error) {
            handleErrorAlert(setAlertData);
        } finally {
            setExcludeLoading(false);
        }
    };

    const handleDoNotShowAgainClick = async (cardData, source) => {
        try {
            setDoNotShowAgainLoading(true);

            const plaid_transaction_ids = [];

            if (data.matches && data.matches?.length) {
                for (const match of data.matches) {
                    plaid_transaction_ids.push(match.transaction_id);
                }
            }

            if (data.needs_user_input && data.needs_user_input?.length) {
                for (const transaction of data.needs_user_input[0]?.transactions) {
                    plaid_transaction_ids.push(transaction.id);
                }
            }

            const response = await excludeTransactionsFromAgentMatching({
                company_id: companyId,
                plaid_transaction_ids,
            });

            handleSuccessAlert(setAlertData, response?.message);

            await skipMatch(cardData, source);
        } catch (error) {
            handleErrorAlert(setAlertData);
        } finally {
            setDoNotShowAgainLoading(false);
        }
    };

    const handleGetAgentMatchings = async (filterValues) => {
        try {
            const response = await getAgentMatchings({ companyId, ...filterValues });
            setData(response);

            const hasMatches = response?.matches?.length > 0;
            const needsInput = response?.needs_user_input?.length > 0;

            if (!hasMatches && !needsInput) {
                setNoDataFound(true);
            } else {
                setNoDataFound(false);
            }
        } catch (error) {
            handleErrorAlert(setAlertData, 'Error fetching agent matchings');
            setNoDataFound(true);
        }
    };

    const handleFiltersSubmit = (values) => {
        setFilterValues(values);
        const skipId = Object.keys(values).length === 0 ? null : skipTransactionId;
        handleSkipAgentMatching(skipId, values);
    };

    const renderCompletedMatchCard = (index) => {
        const cardData = data.matches[index];
        return (
            <CompletedMatchCard
                cardData={cardData}
                index={index}
                classes={classes}
                handleEmailSourceClick={handleEmailSourceClick}
                skipMatch={skipMatch}
                handleMatchClick={handleMatchClick}
                excludeLoading={excludeLoading}
                handleExcludeClick={handleExcludeClick}
                doNotShowAgainLoading={doNotShowAgainLoading}
                handleDoNotShowAgainClick={handleDoNotShowAgainClick}
            />
        );
    };

    const renderCompletedUserInputCard = (index) => {
        const cardData = data.needs_user_input[index];
        return (
            <CompletedUserInputCard
                cardData={cardData}
                index={index}
                classes={classes}
                data={data}
                setData={setData}
                handleEmailSourceClick={handleEmailSourceClick}
                skipMatch={skipMatch}
                handleMatchClick={handleMatchClick}
                matchLoading={matchLoading}
                excludeLoading={excludeLoading}
                handleExcludeClick={handleExcludeClick}
                doNotShowAgainLoading={doNotShowAgainLoading}
                handleDoNotShowAgainClick={handleDoNotShowAgainClick}
            />
        );
    };

    const renderCurrentMatchCard = () => {
        return (
            <CurrentMatchCard
                currentCardIndex={currentCardIndex}
                data={data}
                renderingStage={renderingStage}
                advanceRenderingStage={advanceRenderingStage}
                handleEmailSourceClick={handleEmailSourceClick}
                skipMatch={skipMatch}
                handleMatchClick={handleMatchClick}
                classes={classes}
                currentSection={currentSection}
                excludeLoading={excludeLoading}
                handleExcludeClick={handleExcludeClick}
                doNotShowAgainLoading={doNotShowAgainLoading}
                handleDoNotShowAgainClick={handleDoNotShowAgainClick}
            />
        );
    };

    const renderCurrentUserInputCard = () => {
        return (
            <CurrentUserInputCard
                currentCardIndex={currentCardIndex}
                data={data}
                renderingStage={renderingStage}
                advanceRenderingStage={advanceRenderingStage}
                handleEmailSourceClick={handleEmailSourceClick}
                skipMatch={skipMatch}
                handleMatchClick={handleMatchClick}
                classes={classes}
                currentSection={currentSection}
                matchLoading={matchLoading}
                excludeLoading={excludeLoading}
                handleExcludeClick={handleExcludeClick}
                doNotShowAgainLoading={doNotShowAgainLoading}
                handleDoNotShowAgainClick={handleDoNotShowAgainClick}
            />
        );
    };

    useEffect(() => {
        const timeout = setTimeout(() => {
            setIntroVisible(false);
        }, 2500);
        return () => clearTimeout(timeout);
    }, []);

    useEffect(() => {
        if (!introVisible && (data?.matches?.length > 0 || data?.needs_user_input?.length > 0)) {
            startCardRendering();
        }
    }, [introVisible, data]);

    useEffect(() => {
        if (companyId) {
            handleGetAgentMatchings(filterValues);
        }
    }, [companyId]);

    useEffect(() => {
        if (messagesEndRef.current) {
            messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
        }
    }, [renderingStage, currentCardIndex, completedCards.length]);

    return (
        <Box sx={classes.mainContainer}>
            <Grid container spacing={2}>
                <Grid item xs={12} display="flex" justifyContent="flex-end">
                    <IconButton onClick={onExit}>
                        <ClearIcon />
                    </IconButton>
                </Grid>

                <Grid item xs={12}>
                    <AgentMatchingFilters
                        handleFiltersSubmit={(values) => handleFiltersSubmit(values)}
                    />
                </Grid>
            </Grid>

            {introVisible || loadingSkip ? (
                <Paper elevation={3} sx={classes.boxStyle}>
                    <Typography variant="h5" fontWeight="bold" mb={1}>
                        AI Matching in Progress
                    </Typography>
                    <Typography variant="body1" mb={3}>
                        Please wait while we intelligently pair transactions with inventory...
                    </Typography>
                    <Typography variant="body1" mb={3}>
                        AI is thinking...
                    </Typography>
                </Paper>
            ) : noDataFound ? (
                <Paper elevation={3} sx={classes.datafoundbox}>
                    <Typography variant="h6" fontWeight="bold" mb={2}>
                        No Data Found
                    </Typography>
                    <Typography variant="body1">
                        There are no transactions or inventory items to match.
                    </Typography>
                </Paper>
            ) : (
                <Box display="flex" flexDirection="column" alignItems="center" width="100%" mt={1}>
                    <Typography sx={classes.textTheme} variant="h5" fontWeight="bold" mb={4}>
                        AI Matching Results
                    </Typography>

                    {!showFinalMessage &&
                        completedCards.map((card) => {
                            if (card.section === 'matches') {
                                return renderCompletedMatchCard(card.index);
                            } else if (card.section === 'needs_user_input') {
                                return renderCompletedUserInputCard(card.index);
                            }
                            return null;
                        })}

                    {showFinalMessage ? (
                        <Paper elevation={3} sx={classes.finalMessage}>
                            <Typography variant="h6" fontWeight="bold" mb={2}>
                                🎉 All transactions have been matched!
                            </Typography>
                            <Typography variant="body1" className="pulse">
                                Exiting...
                            </Typography>
                        </Paper>
                    ) : (
                        !completedCards.some(
                            (c) => c.section === currentSection && c.index === currentCardIndex,
                        ) &&
                        (currentSection === 'matches'
                            ? renderCurrentMatchCard()
                            : renderCurrentUserInputCard())
                    )}

                    <div ref={messagesEndRef} />

                    {showEmailSourceModal && selectedEmailData && (
                        <EmailSourceModal
                            emailId={selectedEmailData}
                            showEmailSourceModal={showEmailSourceModal}
                            setShowEmailSourceModal={setShowEmailSourceModal}
                        />
                    )}

                    <SnackBarAlert alertData={alertData} setAlertData={setAlertData} />
                </Box>
            )}

            <style>
                {`
                @keyframes fadeIn {
                    from { opacity: 0; transform: translateY(20px); }
                    to { opacity: 1; transform: translateY(0); }
                }

                @keyframes pulse {
                    0% { opacity: 1; }
                    50% { opacity: 0.5; }
                    100% { opacity: 1; }
                }
                
                .pulse {
                    animation: pulse 1.5s infinite ease-in-out;
                }
                `}
            </style>
        </Box>
    );
};

export default AIMatchingView;
